import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { CohortDimension, CohortFilters } from "../reportTypes";
import { COHORT_DIMENSIONS } from "../reportTypes";
import type { Facet } from "../reportTypes";

/**
 * Cohort filter sidebar — one collapsible dropdown section per dimension
 * (location, iwi, ethnicity, service, engagement duration), each a multi-select
 * checkbox list. Multi-select within a dimension is OR; across dimensions, AND.
 * Options + counts come from facets over the whole population, so every option
 * stays visible while filtering.
 *
 * On wide screens the panel is a persistent left column; on narrow tablet /
 * portrait it collapses behind a "Filters" toggle (a disclosure drawer). All
 * controls are ≥ 44px (--hit-target).
 */
type Props = {
  facets: Record<CohortDimension, Facet[]>;
  filters: CohortFilters;
  onToggle: (dim: CohortDimension, value: string) => void;
  onClear: () => void;
};

function activeCount(f: CohortFilters): number {
  return (
    f.location.length +
    f.iwi.length +
    f.ethnicity.length +
    f.service.length +
    f.durationBand.length
  );
}

export function CohortFilterBar({ facets, filters, onToggle, onClear }: Props) {
  const { t } = useTranslation();
  const active = activeCount(filters);

  // Drawer open/closed on narrow screens (ignored by CSS on wide screens).
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Which dimension sections are expanded. Default: open the ones already in use.
  const [openSections, setOpenSections] = useState<Set<CohortDimension>>(() => {
    const s = new Set<CohortDimension>();
    for (const dim of COHORT_DIMENSIONS) {
      if ((filters[dim] as string[]).length > 0) s.add(dim);
    }
    if (s.size === 0) s.add(COHORT_DIMENSIONS[0]);
    return s;
  });

  const toggleSection = (dim: CohortDimension) =>
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(dim)) next.delete(dim);
      else next.add(dim);
      return next;
    });

  const labelFor = (dim: CohortDimension, value: string): string =>
    dim === "durationBand" ? t(`reports.duration.${value}`) : value;

  return (
    <aside
      className="rpt-sidebar"
      data-open={drawerOpen}
      aria-label={t("reports.filtersLabel")}
    >
      <button
        type="button"
        className="rpt-sidebar__toggle"
        aria-expanded={drawerOpen}
        aria-controls="rpt-sidebar-body"
        onClick={() => setDrawerOpen((o) => !o)}
      >
        {t("reports.filtersTitle")}
        {active > 0 && <span className="rpt-sidebar__badge">{active}</span>}
      </button>

      <div className="rpt-sidebar__body" id="rpt-sidebar-body">
        <div className="rpt-sidebar__head">
          <h2 className="rpt-sidebar__title">{t("reports.filtersTitle")}</h2>
          {active > 0 && (
            <button
              type="button"
              className="rpt-sidebar__clear"
              onClick={onClear}
            >
              {t("reports.clearAll", { count: active })}
            </button>
          )}
        </div>

        {COHORT_DIMENSIONS.map((dim) => {
          const options = facets[dim];
          if (!options || options.length === 0) return null;
          const selected = filters[dim] as string[];
          const isOpen = openSections.has(dim);
          const panelId = `rpt-flt-${dim}`;
          return (
            <div className="rpt-flt" key={dim}>
              <button
                type="button"
                className="rpt-flt__head"
                aria-expanded={isOpen}
                aria-controls={panelId}
                onClick={() => toggleSection(dim)}
              >
                <span className="rpt-flt__label">
                  {t(`reports.filter.${dim}`)}
                </span>
                {selected.length > 0 && (
                  <span className="rpt-flt__count">{selected.length}</span>
                )}
                <span className="rpt-flt__chev" aria-hidden="true">
                  {isOpen ? "▾" : "▸"}
                </span>
              </button>
              <div
                className="rpt-flt__panel"
                id={panelId}
                hidden={!isOpen}
                role="group"
                aria-label={t(`reports.filter.${dim}`)}
              >
                {options.map((opt) => (
                  <label key={opt.value} className="rpt-opt">
                    <input
                      type="checkbox"
                      className="rpt-opt__cb"
                      checked={selected.includes(opt.value)}
                      onChange={() => onToggle(dim, opt.value)}
                    />
                    <span className="rpt-opt__label">
                      {labelFor(dim, opt.value)}
                    </span>
                    <span className="rpt-opt__count">{opt.count}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
}

export default CohortFilterBar;
